import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Text,
  Stack,
  Group,
  Image,
  Divider,
  ActionIcon,
  Title,
  Loader,
} from '@mantine/core';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconBrandX,
  IconBrandYoutube,
  IconMapPin,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface DynamicCompanyInfo {
  name: string;
  logo: string;
  favicon: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    linkedin: string;
    twitter: string;
  };
}

export const Footer: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);
  const staticCompanyInfo = content.companyInfo;
  const staticFooter = content.navigation.footer;

  const [footerLogo, setFooterLogo] = useState<string | null>(null);
  const [socialIcons, setSocialIcons] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [dynamicCompany, setDynamicCompany] = useState<DynamicCompanyInfo | null>(null);
  const [companyLoading, setCompanyLoading] = useState(true);

  const socialIconComponents: Record<string, React.ElementType> = {
    facebook: IconBrandFacebook,
    instagram: IconBrandInstagram,
    linkedin: IconBrandLinkedin,
    whatsapp: IconBrandWhatsapp,
    twitter: IconBrandX,
    youtube: IconBrandYoutube,
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => {
    const loadAll = async () => {
      try {
        // 🔥 CACHE LOAD FIRST
        const cachedIcons = sessionStorage.getItem('footer_icons');
        const cachedCategories = sessionStorage.getItem('footer_categories');
        const cachedCompany = sessionStorage.getItem('footer_company');

        if (cachedIcons) setSocialIcons(JSON.parse(cachedIcons));
        if (cachedCategories) {
          setCategories(JSON.parse(cachedCategories));
          setCategoriesLoading(false);
        }
        if (cachedCompany) {
          setDynamicCompany(JSON.parse(cachedCompany));
          setCompanyLoading(false);
        }

        // 🔥 PARALLEL API CALLS
        const [galleryRes, categoriesRes, companyRes] = await Promise.all([
          axios.get(`${API_URL}/public/gallery/HEADER`),
          axios.get(`${API_URL}/categories/public/with-count`),
          axios.get(`${API_URL}/settings/company`),
        ]);

        // Icons
        const icons: Record<string, string> = {};
        galleryRes.data.items
          .filter((item: any) => item.type === 'ICON' && item.active)
          .forEach((item: any) => {
            const platform = item.title?.toLowerCase() || '';
            if (platform && socialIconComponents[platform]) {
              const iconUrl = item.imageUrl.startsWith('http')
                ? item.imageUrl
                : `${API_URL}${item.imageUrl}`;
              icons[platform] = iconUrl;
            }
          });

        setSocialIcons(icons);
        sessionStorage.setItem('footer_icons', JSON.stringify(icons));
        setLoading(false);

        // Categories
        setCategories(categoriesRes.data);
        sessionStorage.setItem('footer_categories', JSON.stringify(categoriesRes.data));
        setCategoriesLoading(false);

        // Company
        setDynamicCompany(companyRes.data);
        sessionStorage.setItem('footer_company', JSON.stringify(companyRes.data));
        setCompanyLoading(false);

      } catch (error) {
        console.error('Footer error:', error);
        setLoading(false);
        setCategoriesLoading(false);
        setCompanyLoading(false);
      }
    };

    loadAll();
  }, []);

  const companyInfo = dynamicCompany || staticCompanyInfo;
  const footer = staticFooter;

  const socialMediaLinks = companyInfo.socialMedia || footer.socialMedia;

  const renderSocialIcon = (platform: string, url: string) => {
    const IconComponent = socialIconComponents[platform];
    const customIcon = socialIcons[platform];

    if (customIcon) {
      return (
        <ActionIcon key={platform} component="a" href={url} target="_blank" size="lg">
          <Image src={customIcon} width={20} height={20} />
        </ActionIcon>
      );
    } else if (IconComponent) {
      return (
        <ActionIcon key={platform} component="a" href={url} target="_blank" size="lg">
          <IconComponent size={20} />
        </ActionIcon>
      );
    }
    return null;
  };

  const isLoading = loading || categoriesLoading || companyLoading;

  return (
    <footer>
      <div style={{ backgroundColor: '#1a2e1a', color: 'white' }}>
        <Container size="xl" py={50}>
          <Grid>

            {/* Company Info */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <Group gap="xs">
                  <Image
                    src="/assets/images/logo.png"
                    alt={companyInfo.name}
                    height={50}
                    width="auto"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                  <Text size="xl" fw={700}>
                    {companyInfo.name}
                  </Text>
                </Group>

                <Group gap="xs"><IconMapPin size={18} /><Text size="sm">{companyInfo.address}</Text></Group>
                <Group gap="xs"><IconPhone size={18} /><Text size="sm">{companyInfo.phone}</Text></Group>
                <Group gap="xs"><IconMail size={18} /><Text size="sm">{companyInfo.email}</Text></Group>
              </Stack>
            </Grid.Col>

            {/* Company Links */}
            <Grid.Col span={{ base: 6, md: 2 }}>
              <Stack gap="md">
                <Title order={4} c="white">Company</Title>
                {footer.companyLinks.map((link) => (
                  <Text key={link.id} component={Link} to={link.path} onClick={scrollToTop} size="sm" c="gray.3">
                    {link.label}
                  </Text>
                ))}
              </Stack>
            </Grid.Col>

            {/* Quick Links */}
            <Grid.Col span={{ base: 6, md: 2 }}>
              <Stack gap="md">
                <Title order={4} c="white">Quick Links</Title>
                {footer.quickLinks.map((link) => (
                  <Text key={link.id} component={Link} to={link.path} onClick={scrollToTop} size="sm" c="gray.3">
                    {link.label}
                  </Text>
                ))}
              </Stack>
            </Grid.Col>

            {/* Products */}
            <Grid.Col span={{ base: 6, md: 2 }}>
              <Stack gap="md">
                <Title order={4} c="white">Products</Title>
                {categoriesLoading ? (
                  <Loader size="xs" color="white" />
                ) : (
                  categories.map((category) => (
                    <Text key={category.id} component={Link} to={`/products?category=${category.slug || category.id}`} size="sm" c="gray.3">
                      {category.name}
                    </Text>
                  ))
                )}
              </Stack>
            </Grid.Col>

            {/* Social */}
            <Grid.Col span={{ base: 6, md: 2 }}>
              <Stack gap="md">
                <Title order={4} c="white">Connect</Title>
                <Group gap="xs">
                  {isLoading ? (
                    <Loader size="sm" color="white" />
                  ) : (
                    Object.entries(socialMediaLinks).map(
                      ([key, value]) => value && renderSocialIcon(key, value)
                    )
                  )}
                </Group>
              </Stack>
            </Grid.Col>

          </Grid>

          <Divider my="xl" color="gray.7" />

          <Text ta="center" size="sm" c="gray.4">
            {footer.copyright}
          </Text>
        </Container>
      </div>
    </footer>
  );
};